import ExcelJS from 'exceljs';
import type { Style, Worksheet } from 'exceljs';
import { roundOff } from '../../../../Components/helper';
import { toSafeSheetName } from '../../../../utils/excel-sheet-name';
import { toIdString } from './calc';
import { getSampleRefLabel, getValue, setCurrency } from './costing-todo.helpers';
import type { CostingExpandedData, CostingRecord, CostingTemplate, LandedData, TemplateColumn } from './store/Costing.type';

/**
 * Reads the stored per-combination price for one exported row.
 * Reads the same `combinations` entry the grid's Price Per Unit column shows, so a spreadsheet and
 * the grid never disagree on that column.
 * @param costingItem the costing being exported
 * @param expandedItem the combination row being exported, when the costing is expanded
 * @returns the stored price, or '0'
 */
function readExpandedPricePerUnit(costingItem: CostingRecord | undefined, expandedItem: CostingExpandedData | null | undefined): string {
  const source = expandedItem ?? costingItem?.expandedData?.[0];
  if (!source) return '0';
  const colourwayId = toIdString(source.colourway ?? null);
  const lengthId = toIdString(source.length ?? null);
  const size = source.size ?? null;
  const combination = (costingItem?.combinations ?? []).find((entry) => entry.colourwayId === colourwayId && entry.lengthId === lengthId && (entry.size ?? null) === size);
  return combination?.pricePerUnit || '0';
}

/* Client-side Excel export for the Costing TODO table. Extracted from the JavaScript component
   body, where these seven functions sat after `return` and relied on hoisting. Logic is a
   verbatim port; only the closure over component state became the explicit context object. */

type ExcelStyleKey = 'header' | 'dataCell' | 'numberCell' | 'summaryCell' | 'averageCell' | 'summaryLabelCell';
type ExcelStyles = Record<ExcelStyleKey, Partial<Style>>;

/** Everything the export reads out of the component. Nothing React- or Redux-shaped. */
export interface CostingExcelContext {
  selectedColumns: string[];
  topColumnsArr: TemplateColumn[] | undefined;
  defaultColumnsArr: TemplateColumn[] | undefined;
  bottomColumnsArr: TemplateColumn[] | undefined;
  activeCostingTemplateDetailes: CostingTemplate | undefined;
}

interface ExpandedPair {
  costingItem: CostingRecord;
  expandedItem: CostingExpandedData | null | undefined;
}

/**
 * parseFloat with the original's implicit string coercion made explicit. JavaScript's
 * parseFloat stringifies its argument, so numeric and string inputs behave identically.
 */
const parseNumeric = (value: unknown): number => Number.parseFloat(String(value));

/**
 * Workbook cell styles. The explicit return type is load-bearing: without it TypeScript widens
 * 'pattern' / 'solid' / 'center' to `string`, which does not satisfy ExcelJS's Fill and
 * Alignment literal unions.
 */
function createExcelStyles(): ExcelStyles {
  return {
    header: {
      font: { name: 'Aktiv Grotesk', bold: false, size: 10, color: { argb: '000000' } },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F3F3F3' } },
      alignment: { horizontal: 'center', vertical: 'middle' },
    },
    dataCell: {
      font: { name: 'Aktiv Grotesk', size: 10 },
      alignment: { horizontal: 'left', vertical: 'middle' },
    },
    numberCell: {
      font: { name: 'Aktiv Grotesk', size: 10 },
      alignment: { horizontal: 'right', vertical: 'middle' },
      numFmt: '#,##0.00',
    },
    summaryCell: {
      font: { name: 'Aktiv Grotesk', bold: false, size: 10, color: { argb: '000000' } },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '1E90FF' } },
      alignment: { horizontal: 'right', vertical: 'middle' },
      numFmt: '#,##0.00',
    },
    averageCell: {
      font: { name: 'Aktiv Grotesk', bold: false, size: 10, color: { argb: '000000' } },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF892F' } },
      alignment: { horizontal: 'right', vertical: 'middle' },
      numFmt: '#,##0.00',
    },
    summaryLabelCell: {
      font: { name: 'Aktiv Grotesk', bold: false, size: 10, color: { argb: '000000' } },
      alignment: { horizontal: 'center', vertical: 'middle' },
    },
  };
}

/** Builds the header row: fixed product columns, then whatever the column chooser enabled. */
function buildExcelHeaders(ctx: CostingExcelContext): string[] {
  const { selectedColumns, topColumnsArr, defaultColumnsArr, bottomColumnsArr, activeCostingTemplateDetailes } = ctx;

  const headers = ['Product Number', 'Product Description', 'Brand', 'Sub Brand', 'Season', 'Month', 'Year', 'Category 1', 'Category 2', 'Category 3', 'Category 4'];

  if (selectedColumns?.includes('reference')) {
    headers.push('Reference');
  }

  if (selectedColumns?.includes('developmentOrder')) {
    headers.push('Development Order');
  }

  headers.push('Costing Type');

  if (selectedColumns?.includes('sampleRef')) {
    headers.push('Sample Ref');
  }

  if (selectedColumns?.includes('supplier')) {
    headers.push('Supplier');
  }

  if (selectedColumns?.includes('factory')) {
    headers.push('Factory');
  }

  if (selectedColumns?.includes('moqNeeded')) {
    headers.push('MOQ Needed');
  }

  if (selectedColumns?.includes('colourway')) {
    headers.push('Colourway');
  }

  if (selectedColumns?.includes('size')) {
    headers.push('Size');
  }

  if (selectedColumns?.includes('length')) {
    headers.push('Length');
  }

  if (selectedColumns?.includes('incoterm')) {
    headers.push('Incoterm');
  }

  if (selectedColumns?.includes('shipmentMethod')) {
    headers.push('Shipment Method');
  }

  if (selectedColumns?.includes('currency')) {
    headers.push('Currency');
  }

  if (selectedColumns?.includes('targetCost')) {
    headers.push('Target Cost');
  }

  if (selectedColumns?.includes('quantity')) {
    headers.push('Quantity');
  }

  if (selectedColumns?.includes('pricePerUnit')) {
    headers.push('Price Per Unit');
  }

  if (selectedColumns?.includes('pricePerUnitIncoterm')) {
    headers.push('Price Per Unit (Incoterm)');
  }

  if (selectedColumns?.includes('totalIncoterm')) {
    headers.push('Total (Incoterm)');
  }

  if (topColumnsArr?.length) {
    topColumnsArr.forEach((col) => {
      headers.push(col.title);
    });
  }

  if (activeCostingTemplateDetailes?.landedInfo?.length) {
    activeCostingTemplateDetailes.landedInfo
      .filter((col) => col?.isActive)
      .forEach((col) => {
        /* Non-null assertion preserves the original's behaviour: a landedInfo entry without a
           `type` throws here rather than silently rendering an unsuffixed header. */
        headers.push(`${col.title} ${col.type!.includes('percentage') ? '(%)' : ''}`);
      });
  }

  if (defaultColumnsArr?.length) {
    defaultColumnsArr.forEach((col) => {
      headers.push(col.title);
    });
  }

  if (bottomColumnsArr?.length) {
    bottomColumnsArr.forEach((col) => {
      headers.push(col.title);
    });
  }

  headers.push('Status');

  return headers;
}

/** Applies the fixed 20.75 column width across every generated column. */
function setColumnWidths(worksheet: Worksheet, totalColumns: number): void {
  const columnWidth = 20.75;

  for (let i = 1; i <= totalColumns; i++) {
    worksheet.getColumn(i).width = columnWidth;
  }
}

/** Builds one data row: one per expandedData entry, or a single aggregate row when there is none. */
function buildExcelRowData(costingItem: CostingRecord, expandedItem: CostingExpandedData | null | undefined, ctx: CostingExcelContext): Array<string | number> {
  const { selectedColumns, topColumnsArr, defaultColumnsArr, bottomColumnsArr, activeCostingTemplateDetailes } = ctx;
  const rowDataExcel: Array<string | number> = [];

  rowDataExcel.push(costingItem?.number || '--');

  rowDataExcel.push(costingItem?.description || '--');

  rowDataExcel.push(getValue(costingItem?.brandId) || '--');

  rowDataExcel.push(getValue(costingItem?.subBrandId) || '--');

  rowDataExcel.push(getValue(costingItem?.seasonId) || '--');

  rowDataExcel.push(costingItem?.month || '--');

  rowDataExcel.push(costingItem?.year || '--');

  rowDataExcel.push(getValue(costingItem?.catOneId) || '--');

  rowDataExcel.push(getValue(costingItem?.catTwoId) || '--');

  rowDataExcel.push(getValue(costingItem?.catThreeId) || '--');

  rowDataExcel.push(getValue(costingItem?.catFourId) || '--');

  if (selectedColumns?.includes('reference')) {
    rowDataExcel.push(costingItem?.productInfo?.reference || costingItem?.reference || '--');
  }

  if (selectedColumns?.includes('developmentOrder')) {
    rowDataExcel.push(costingItem?.devOrderId || '--');
  }

  rowDataExcel.push(costingItem?.isAverageCosting ? 'Average' : 'Detailed');

  if (selectedColumns?.includes('sampleRef')) {
    rowDataExcel.push(getSampleRefLabel(costingItem?.sampleRef) || '--');
  }

  if (selectedColumns?.includes('supplier')) {
    rowDataExcel.push(costingItem?.supplier?.supplierName || '--');
  }

  if (selectedColumns?.includes('factory')) {
    rowDataExcel.push(getValue(costingItem?.productInfo?.factoryId) || '--');
  }

  if (selectedColumns?.includes('moqNeeded')) {
    rowDataExcel.push(parseNumeric(costingItem?.productInfo?.moqNeeded || 0).toFixed(2));
  }

  if (selectedColumns?.includes('colourway')) {
    if (expandedItem?.colourway) {
      rowDataExcel.push(expandedItem.colourway.name || '--');
    } else {
      rowDataExcel.push(costingItem?.costColourways?.map((cw) => cw?.name).join(', ') || '--');
    }
  }

  if (selectedColumns?.includes('size')) {
    if (expandedItem?.size) {
      rowDataExcel.push(expandedItem.size || '--');
    } else {
      rowDataExcel.push(costingItem?.costSizes?.join(', ') || '--');
    }
  }

  if (selectedColumns?.includes('length')) {
    if (expandedItem?.length) {
      rowDataExcel.push(expandedItem.length?.name || '--');
    } else {
      rowDataExcel.push(costingItem?.costLengths?.map((len) => len?.name).join(', ') || '--');
    }
  }

  if (selectedColumns?.includes('incoterm')) {
    rowDataExcel.push(getValue(costingItem?.productInfo?.incoterms) || '--');
  }

  if (selectedColumns?.includes('shipmentMethod')) {
    rowDataExcel.push(costingItem?.productInfo?.shipmentMethod || '--');
  }

  if (selectedColumns?.includes('currency')) {
    rowDataExcel.push(costingItem?.productInfo?.currency || '--');
  }

  if (selectedColumns?.includes('targetCost')) {
    const currencyVal = setCurrency(costingItem?.productInfo?.currency);
    const targetCost = parseNumeric(costingItem?.productInfo?.targetCost || costingItem?.targetCost || 0).toFixed(2);
    rowDataExcel.push(`${currencyVal}${targetCost}`);
  }

  if (selectedColumns?.includes('quantity')) {
    const quantity = expandedItem?.landedData?.quantityPerSizCol || costingItem?.expandedData?.[0]?.landedData?.quantityPerSizCol || '0';
    rowDataExcel.push(parseNumeric(quantity).toFixed(2));
  }

  if (selectedColumns?.includes('pricePerUnit')) {
    const currencyVal = setCurrency(costingItem?.productInfo?.currency);
    /* The stored per-combination price, matching the grid's Price Per Unit column rather than the
       incoterm roll-up below. */
    rowDataExcel.push(`${currencyVal}${parseNumeric(readExpandedPricePerUnit(costingItem, expandedItem)).toFixed(2)}`);
  }

  if (selectedColumns?.includes('pricePerUnitIncoterm')) {
    const currencyVal = setCurrency(costingItem?.productInfo?.currency);
    const pricePerUnit = expandedItem?.landedData?.pricePerUnitInco || costingItem?.expandedData?.[0]?.landedData?.pricePerUnitInco || '0';
    rowDataExcel.push(`${currencyVal}${parseNumeric(pricePerUnit).toFixed(2)}`);
  }

  if (selectedColumns?.includes('totalIncoterm')) {
    const currencyVal = setCurrency(costingItem?.productInfo?.currency);
    const totalInco = expandedItem?.landedData?.totalInco || costingItem?.expandedData?.[0]?.landedData?.totalInco || '0';
    rowDataExcel.push(`${currencyVal}${parseNumeric(totalInco).toFixed(2)}`);
  }

  const companyCurrency = setCurrency(costingItem?.landedExchangeRateInformation?.companyCurrency);
  const landedData: LandedData | undefined = expandedItem?.landedData || costingItem?.expandedData?.[0]?.landedData;

  if (topColumnsArr?.length) {
    topColumnsArr.forEach((topCol) => {
      const value = landedData?.[topCol.aliasName] || '0';
      if (topCol.aliasName.includes('price') || topCol.aliasName.includes('total') || topCol.aliasName.includes('cost')) {
        rowDataExcel.push(`${companyCurrency}${parseNumeric(value).toFixed(2)}`);
      } else {
        rowDataExcel.push(value as string | number);
      }
    });
  }

  if (activeCostingTemplateDetailes?.landedInfo?.length) {
    activeCostingTemplateDetailes.landedInfo
      .filter((col) => col?.isActive)
      .forEach((landedCol) => {
        if (landedData?.landedInfo) {
          let value: string | number = landedCol.tempValue || '0';
          let calculatedValue: string | number | null = null;

          const foundInfo = landedData.landedInfo.find((info) => info?._id === landedCol?._id);
          if (foundInfo) {
            value = foundInfo.value || foundInfo.tempValue || value;
            calculatedValue = foundInfo.calculatedValue ?? null;
          }

          if (landedCol.type === 'percentage') {
            if (calculatedValue && calculatedValue !== '' && calculatedValue !== '0') {
              rowDataExcel.push(`${companyCurrency}${parseNumeric(calculatedValue).toFixed(2)} (${parseNumeric(value).toFixed(2)}%)`);
            } else {
              rowDataExcel.push(`${parseNumeric(value).toFixed(2)}%`);
            }
          } else if (landedCol.type === 'amount') {
            const displayValue = calculatedValue || value;
            rowDataExcel.push(`${companyCurrency}${parseNumeric(displayValue).toFixed(2)}`);
          } else {
            const displayValue = calculatedValue || value;
            rowDataExcel.push(displayValue);
          }
        } else {
          rowDataExcel.push('--');
        }
      });
  }

  if (defaultColumnsArr?.length) {
    defaultColumnsArr.forEach((defaultCol) => {
      if (landedData) {
        const baseValue = landedData[defaultCol.aliasName] || defaultCol.tempValue || '0';
        const calculatedField = `${defaultCol.aliasName}CalculatedValue`;
        const calculatedValue = landedData[calculatedField];

        if (defaultCol.type === 'percentage') {
          if (calculatedValue && calculatedValue !== '' && calculatedValue !== '0') {
            rowDataExcel.push(`${companyCurrency}${parseNumeric(calculatedValue).toFixed(2)} (${parseNumeric(baseValue).toFixed(2)}%)`);
          } else {
            rowDataExcel.push(`${parseNumeric(baseValue).toFixed(2)}%`);
          }
        } else {
          const value = calculatedValue || baseValue;
          if (
            defaultCol.aliasName.includes('fee') ||
            defaultCol.aliasName.includes('royalty') ||
            defaultCol.aliasName.includes('license') ||
            defaultCol.aliasName.includes('likeness')
          ) {
            if (calculatedValue && calculatedValue !== '' && calculatedValue !== '0' && baseValue && baseValue !== '0') {
              rowDataExcel.push(`${companyCurrency}${parseNumeric(calculatedValue).toFixed(2)} (${companyCurrency}${parseNumeric(baseValue).toFixed(2)})`);
            } else {
              rowDataExcel.push(`${companyCurrency}${parseNumeric(value).toFixed(2)}`);
            }
          } else {
            rowDataExcel.push(`${companyCurrency}${parseNumeric(value).toFixed(2)}`);
          }
        }
      } else {
        rowDataExcel.push('--');
      }
    });
  }

  if (bottomColumnsArr?.length) {
    bottomColumnsArr.forEach((bottomCol) => {
      if (landedData) {
        const baseValue = landedData[bottomCol.aliasName] || '0';
        const calculatedField = `${bottomCol.aliasName}CalculatedValue`;
        const calculatedValue = landedData[calculatedField];

        if (bottomCol.title === 'Gross Profit Per Unit') {
          const value = calculatedValue || baseValue;
          rowDataExcel.push(`${companyCurrency}${parseNumeric(value).toFixed(2)}`);
        } else if (bottomCol.title === 'Markup Per Unit') {
          const value = calculatedValue || baseValue;
          rowDataExcel.push(`${companyCurrency}${parseNumeric(value).toFixed(2)}`);
        } else if (bottomCol.title === 'Margin % Per Unit' || bottomCol.title === 'Retail Margin % Per Unit') {
          const value = calculatedValue || baseValue;
          rowDataExcel.push(parseNumeric(value).toFixed(2));
        } else if (
          bottomCol.aliasName.includes('price') ||
          bottomCol.aliasName.includes('total') ||
          bottomCol.aliasName.includes('profit') ||
          bottomCol.aliasName.includes('value') ||
          bottomCol.aliasName.includes('selling') ||
          bottomCol.aliasName.includes('landed')
        ) {
          const value = calculatedValue || baseValue;
          rowDataExcel.push(`${companyCurrency}${parseNumeric(value).toFixed(2)}`);
        } else if (
          bottomCol.aliasName.includes('margin') ||
          bottomCol.aliasName.includes('markup') ||
          bottomCol.aliasName.includes('discount') ||
          bottomCol.aliasName.includes('vat') ||
          bottomCol.aliasName.includes('customerDiscount')
        ) {
          if (calculatedValue && calculatedValue !== '' && calculatedValue !== '0') {
            rowDataExcel.push(`${companyCurrency}${parseNumeric(calculatedValue).toFixed(2)} (${parseNumeric(baseValue).toFixed(2)}%)`);
          } else {
            rowDataExcel.push(`${parseNumeric(baseValue).toFixed(2)}%`);
          }
        } else {
          const value = calculatedValue || baseValue;
          if (!isNaN(parseNumeric(value)) && parseNumeric(value) > 0) {
            rowDataExcel.push(`${companyCurrency}${parseNumeric(value).toFixed(2)}`);
          } else {
            rowDataExcel.push(value as string | number);
          }
        }
      } else {
        rowDataExcel.push('--');
      }
    });
  }

  rowDataExcel.push(costingItem?.status || '--');

  return rowDataExcel;
}

/**
 * Appends the totals row. Columns whose header names a total or quantity are summed (blue);
 * margin columns are averaged and rendered as a percentage (orange); everything else is blank.
 */
function addSummaryRow(worksheet: Worksheet, styles: ExcelStyles, allExpandedData: ExpandedPair[], headers: string[], currentRow: number, ctx: CostingExcelContext): number {
  const { topColumnsArr, defaultColumnsArr, bottomColumnsArr, activeCostingTemplateDetailes } = ctx;
  const summaryRowData: Array<{ value: string | number; style: Partial<Style> }> = [];

  headers.forEach((header, index) => {
    let value: string | number = '';
    let cellStyle = styles.summaryLabelCell;

    const isSumColumn =
      header.toLowerCase().includes('total (incoterm)') ||
      header.toLowerCase().includes('total (converted)') ||
      header.toLowerCase().includes('total (landed)') ||
      header.toLowerCase().includes('quantity');

    const isAverageColumn = header.toLowerCase().includes('margin') || header.toLowerCase().includes('retail margin');

    if (index === 0) {
      value = '';
      cellStyle = styles.summaryLabelCell;
    } else if (isSumColumn) {
      let sum = 0;
      allExpandedData.forEach(({ costingItem, expandedItem }) => {
        let numericValue = 0;
        const landedData = expandedItem?.landedData || costingItem?.expandedData?.[0]?.landedData;

        if (header.toLowerCase().includes('total (incoterm)')) {
          numericValue = parseNumeric(landedData?.totalInco || 0);
        } else if (header.toLowerCase().includes('quantity')) {
          numericValue = parseNumeric(landedData?.quantityPerSizCol || 0);
        } else if (header.toLowerCase().includes('total')) {
          if (topColumnsArr?.length) {
            const topCol = topColumnsArr.find((col) => col.title === header);
            if (topCol) {
              numericValue = parseNumeric(landedData?.[topCol.aliasName] || 0);
            }
          }
          if (defaultColumnsArr?.length) {
            const defaultCol = defaultColumnsArr.find((col) => col.title === header);
            if (defaultCol) {
              const calcValue = landedData?.[`${defaultCol.aliasName}CalculatedValue`] || landedData?.[defaultCol.aliasName] || 0;
              numericValue = parseNumeric(calcValue);
            }
          }
          if (bottomColumnsArr?.length) {
            const bottomCol = bottomColumnsArr.find((col) => col.title === header);
            if (bottomCol) {
              numericValue = parseNumeric(landedData?.[bottomCol.aliasName] || 0);
            }
          }
          if (activeCostingTemplateDetailes?.landedInfo?.length) {
            const landedCol = activeCostingTemplateDetailes.landedInfo.find((col) => header.includes(col.title) && col.title.toLowerCase().includes('total'));
            if (landedCol && landedData?.landedInfo) {
              const foundInfo = landedData.landedInfo.find((info) => info._id === landedCol._id);
              if (foundInfo) {
                if (landedCol.type === 'percentage') {
                  numericValue = parseNumeric(foundInfo.calculatedValue || 0);
                } else {
                  numericValue = parseNumeric(foundInfo.tempValue || foundInfo.value || 0);
                }
              }
            }
          }
        }
        sum += numericValue;
      });

      if (header.toLowerCase().includes('quantity')) {
        value = roundOff(sum, 0, true);
      } else {
        const sampleData = allExpandedData[0];
        let currencyVal = '';
        if (header.toLowerCase().includes('incoterm')) {
          currencyVal = sampleData?.costingItem?.productInfo?.currency || '';
        } else {
          currencyVal = sampleData?.costingItem?.landedExchangeRateInformation?.companyCurrency || '';
        }
        const currencySymbol = setCurrency(currencyVal);
        value = `${currencySymbol}${roundOff(sum, 2, true)}`;
      }
      cellStyle = styles.summaryCell;
    } else if (isAverageColumn) {
      let sum = 0;
      let count = 0;

      allExpandedData.forEach(({ costingItem, expandedItem }) => {
        let numericValue = 0;
        const landedData = expandedItem?.landedData || costingItem?.expandedData?.[0]?.landedData;

        if (topColumnsArr?.length) {
          const topCol = topColumnsArr.find((col) => col.title === header);
          if (topCol) {
            numericValue = parseNumeric(landedData?.[topCol.aliasName] || 0);
          }
        }
        if (defaultColumnsArr?.length) {
          const defaultCol = defaultColumnsArr.find((col) => col.title === header);
          if (defaultCol) {
            const calcValue = landedData?.[`${defaultCol.aliasName}CalculatedValue`] || landedData?.[defaultCol.aliasName] || 0;
            numericValue = parseNumeric(calcValue);
          }
        }
        if (bottomColumnsArr?.length) {
          const bottomCol = bottomColumnsArr.find((col) => col.title === header);
          if (bottomCol) {
            numericValue = parseNumeric(landedData?.[bottomCol.aliasName] || 0);
          }
        }

        sum += numericValue;
        count++;
      });

      if (count > 0) {
        const average = sum / count;
        value = `${roundOff(average, 2, true)}%`;
      } else {
        value = '0%';
      }
      cellStyle = styles.averageCell;
    } else {
      value = '';
      cellStyle = styles.summaryLabelCell;
    }

    summaryRowData.push({ value, style: cellStyle });
  });

  summaryRowData.forEach((cellData, index) => {
    const cell = worksheet.getCell(currentRow, index + 1);
    cell.value = cellData.value;
    cell.style = cellData.style;
  });

  return currentRow + 1;
}

/** Serialises the workbook and triggers the browser download. */
async function saveExcelFile(workbook: ExcelJS.Workbook): Promise<void> {
  const timestamp = new Date().toISOString().split('T')[0] ?? '';
  const filename = `Costing_TODO_List_${timestamp}.xlsx`;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer as unknown as ArrayBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Generates and downloads the Costing TODO workbook for the supplied rows. Emits one worksheet
 * row per expandedData entry, followed by a totals row. Failures are logged and swallowed,
 * matching the original — the user sees no toast.
 */
export async function downloadCostingTodoExcelSheet(selectedCostingData: CostingRecord[], ctx: CostingExcelContext): Promise<void> {
  try {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'Bombiix PLM';
    workbook.lastModifiedBy = 'System';
    workbook.created = new Date();
    workbook.modified = new Date();

    const templateName = ctx.activeCostingTemplateDetailes?.name || 'Default';
    const worksheet = workbook.addWorksheet(toSafeSheetName(`Costing Todo (${templateName})`, 'Costing Todo'));

    const styles = createExcelStyles();

    const headers = buildExcelHeaders(ctx);

    setColumnWidths(worksheet, headers.length);

    let currentRow = 1;

    headers.forEach((header, index) => {
      const cell = worksheet.getCell(currentRow, index + 1);
      cell.value = header;
      cell.style = styles.header;
    });
    currentRow++;

    const allExpandedData: ExpandedPair[] = [];

    for (const costingItem of selectedCostingData) {
      const expandedData = costingItem?.expandedData || [];

      if (expandedData.length > 0) {
        for (const expandedItem of expandedData) {
          const rowDataExcel = buildExcelRowData(costingItem, expandedItem, ctx);
          allExpandedData.push({ costingItem, expandedItem });

          rowDataExcel.forEach((value, index) => {
            const cell = worksheet.getCell(currentRow, index + 1);
            cell.value = value;

            if (typeof value === 'string' && (value.includes('$') || value.includes('£') || value.includes('€') || value.includes('₹') || /^\d+\.?\d*$/.test(value))) {
              cell.style = styles.numberCell;
            } else {
              cell.style = styles.dataCell;
            }
          });
          currentRow++;
        }
      } else {
        const rowDataExcel = buildExcelRowData(costingItem, null, ctx);
        allExpandedData.push({ costingItem, expandedItem: costingItem?.expandedData?.[0] });

        rowDataExcel.forEach((value, index) => {
          const cell = worksheet.getCell(currentRow, index + 1);
          cell.value = value;

          if (typeof value === 'string' && (value.includes('$') || value.includes('£') || value.includes('€') || value.includes('₹') || /^\d+\.?\d*$/.test(value))) {
            cell.style = styles.numberCell;
          } else {
            cell.style = styles.dataCell;
          }
        });
        currentRow++;
      }
    }

    currentRow = addSummaryRow(worksheet, styles, allExpandedData, headers, currentRow, ctx);

    await saveExcelFile(workbook);
  } catch (error) {
    console.error('Error generating Excel file:', error);
  }
}
